/**
 * PupRing Custom JavaScript - Bundle Builder Photo Module
 * ========================================================
 * Buy 3 for $99 Bundle Builder - Photo Upload via UploadCare
 *
 * Features:
 * - Direct UploadCare integration (same as UploadKit)
 * - Lazy loading of UploadCare widget script
 * - Support for Together mode (one photo for all products)
 * - Support for Individual mode (per-product photos)
 * - Photo preview and change functionality
 *
 * Dependencies: custom-bundle-state.js (BundleStateManager)
 *
 * Photo URL format: https://ucarecdn.com/{UUID}/
 */

/**
 * BundlePhotoUploader - Direct UploadCare integration for bundle builder
 *
 * Uses UploadCare (the underlying service of UploadKit) directly.
 * Same CDN, same public key, same fulfillment workflow.
 */
class BundlePhotoUploader {
  // ═══════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════

  static UPLOADCARE_PUBLIC_KEY = '17e6328bac29ad4f6365';
  static CDN_BASE = 'https://ucarecdn.com';
  static SCRIPT_URL = 'https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js';

  // BUG-050 FIX: Static registry survives instance cleanup
  static pendingUploads = new Map();
  static UPLOAD_TIMEOUT_MS = 60000; // 60 seconds max

  // Phase 1 FIX: Managed cleanup interval (singleton across all instances)
  static _cleanupIntervalId = null;

  // Phase 1 ENHANCEMENT: Self-terminating interval counters
  static _emptyCheckCount = 0;
  static _EMPTY_CHECK_THRESHOLD = 5; // Stop after 5 empty checks (2.5 min idle)

  static cleanupStaleUploads() {
    const now = Date.now();
    for (const [uploadId, context] of BundlePhotoUploader.pendingUploads) {
      if (now > context.expiresAt) {
        BundlePhotoUploader.pendingUploads.delete(uploadId);
      }
    }

    // Phase 1 ENHANCEMENT: Self-terminate when idle
    if (BundlePhotoUploader.pendingUploads.size === 0) {
      BundlePhotoUploader._emptyCheckCount++;
      if (BundlePhotoUploader._emptyCheckCount >= BundlePhotoUploader._EMPTY_CHECK_THRESHOLD) {
        BundlePhotoUploader._stopCleanupInterval();
      }
    } else {
      // Reset counter if uploads exist
      BundlePhotoUploader._emptyCheckCount = 0;
    }
  }

  /**
   * Phase 1 FIX: Stop the cleanup interval when idle
   * Called when pendingUploads is empty for too long (2.5 min idle)
   */
  static _stopCleanupInterval() {
    if (BundlePhotoUploader._cleanupIntervalId) {
      clearInterval(BundlePhotoUploader._cleanupIntervalId);
      BundlePhotoUploader._cleanupIntervalId = null;
    }
    // Reset counter for next time interval starts
    BundlePhotoUploader._emptyCheckCount = 0;
  }

  /**
   * Escape a value for use inside a double-quoted HTML attribute
   * @param {string} value - Raw value
   * @returns {string} Escaped value
   */
  static escapeAttribute(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager) {
    this.stateManager = stateManager;
    this.widget = null;
    this.container = null;
    this.currentMode = null;        // 'together' | 'individual'
    this.currentProductIndex = null;
    this.onUploadCallback = null;
    this.isLoading = false;
    this.uploadId = null;           // BUG-050 FIX: Unique ID for current upload session

    // Phase 1 FIX: Start cleanup interval (instance-managed, not module-level)
    this._startCleanupInterval();
  }

  // Phase 1 FIX: Managed interval for stale upload cleanup
  _startCleanupInterval() {
    // Only one interval across all instances (singleton pattern)
    if (!BundlePhotoUploader._cleanupIntervalId) {
      BundlePhotoUploader._cleanupIntervalId = setInterval(() => {
        BundlePhotoUploader.cleanupStaleUploads();
      }, 30000);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // UPLOADCARE SCRIPT LOADING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Load UploadCare script dynamically
   * @returns {Promise<object>} uploadcare global object
   */
  async loadUploadCare() {
    // Already loaded
    if (window.uploadcare) {
      return window.uploadcare;
    }

    // Script already loading
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = BundlePhotoUploader.SCRIPT_URL;
      script.async = true;

      script.onload = () => {
        if (window.uploadcare) {
          resolve(window.uploadcare);
        } else {
          this._loadPromise = null;
          reject(new Error('UploadCare failed to initialize'));
        }
      };

      script.onerror = () => {
        this._loadPromise = null;
        reject(new Error('Failed to load UploadCare script'));
      };

      document.head.appendChild(script);
    });

    return this._loadPromise;
  }

  // ═══════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize for Together mode (one photo for all 3 products)
   * @param {HTMLElement} container - Container element
   * @param {string|null} existingUrl - Existing photo URL if any
   * @param {Function} onUpload - Callback when photo is uploaded
   */
  async initForTogether(container, existingUrl, onUpload) {
    this.cleanup();
    this.container = container;
    this.currentMode = 'together';
    this.currentProductIndex = null;
    this.onUploadCallback = onUpload;

    // BUG-050 FIX: Generate unique upload ID for this session
    this.uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this._bindChangeHandler();
    await this._render(existingUrl);
  }

  /**
   * Initialize for Individual mode (per-product photo)
   * @param {HTMLElement} container - Container element
   * @param {number} productIndex - Product index (0, 1, or 2)
   * @param {string|null} existingUrl - Existing photo URL if any
   * @param {Function} onUpload - Callback when photo is uploaded
   */
  async initForIndividual(container, productIndex, existingUrl, onUpload) {
    this.cleanup();
    this.container = container;
    this.currentMode = 'individual';
    this.currentProductIndex = productIndex;
    this.onUploadCallback = onUpload;

    // BUG-050 FIX: Generate unique upload ID for this session
    this.uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this._bindChangeHandler();
    await this._render(existingUrl);
  }

  /**
   * Get current photo URL
   * @returns {string|null}
   */
  getPhotoUrl() {
    const state = this.stateManager.get();

    if (this.currentMode === 'together') {
      return state.unifiedCustomization?.photoUrl || null;
    } else if (this.currentMode === 'individual' && this.currentProductIndex !== null) {
      return state.products[this.currentProductIndex]?.customization?.photoUrl || null;
    }

    return null;
  }

  /**
   * Check if photo has been uploaded
   * @returns {boolean}
   */
  hasPhoto() {
    return !!this.getPhotoUrl();
  }

  /**
   * Initialize for Quick-View modal context
   * Simpler than together/individual - just captures URL via callback
   * @param {HTMLElement} container - Container element
   * @param {Function} onUpload - Callback when photo is uploaded (receives URL or null)
   */
  async initForQuickView(container, onUpload) {
    this.cleanup();
    this.container = container;
    this.currentMode = 'quickview';
    this.currentProductIndex = null;
    this.onUploadCallback = onUpload;

    // Generate unique upload ID for this session
    this.uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this._bindChangeHandler();

    // Clear container and show loading
    container.innerHTML = this._renderLoading();

    try {
      // Load UploadCare script
      await this.loadUploadCare();

      // Render uploader (no existing photo in quick-view context)
      container.innerHTML = this._renderQuickViewUploader();
      await this._initWidget();
    } catch (error) {
      container.innerHTML = this._renderError(error.message);
    }
  }

  /**
   * Render compact uploader for quick-view modal
   */
  _renderQuickViewUploader() {
    return `
      <div class="bundle-photo-dropzone bundle-photo-dropzone--quickview" id="bundle-uploadcare-quickview">
        <input type="hidden"
               role="uploadcare-uploader"
               data-public-key="${BundlePhotoUploader.UPLOADCARE_PUBLIC_KEY}"
               data-images-only="true"
               data-preview-step="true"
               data-crop="free"
               data-image-shrink="1024x1024"
               data-tabs="file camera url"
               data-clearable="true" />
        <div class="bundle-photo-prompt bundle-photo-prompt--compact">
          <svg class="mb-1" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"/>
          </svg>
          <p class="mb-0 small">Click to upload photo</p>
        </div>
      </div>
    `;
  }

  /**
   * Cleanup widget and event listeners
   */
  cleanup() {
    if (this.widget) {
      // UploadCare widget cleanup
      try {
        this.widget.inputElement?.remove();
      } catch (e) {
        // Ignore cleanup errors
      }
      this.widget = null;
    }

    if (this.container && this._boundChangeHandler) {
      this.container.removeEventListener('click', this._boundChangeHandler);
    }

    this._boundChangeHandler = null;
    this.container = null;
    this.currentMode = null;
    this.currentProductIndex = null;
    // BUG-050 FIX: Clear upload ID (pending entry will expire or be used by fallback)
    this.uploadId = null;
    this.onUploadCallback = null;
  }

  // ═══════════════════════════════════════════════════════════════
  // PRIVATE METHODS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Render the photo upload UI
   * @param {string|null} existingUrl - Existing photo URL
   */
  async _render(existingUrl) {
    const container = this.container;
    if (!container) return;

    // Show loading state
    container.innerHTML = this._renderLoading();

    try {
      // Load UploadCare script
      await this.loadUploadCare();

      // Uploader was cleaned up or moved to another container while loading
      if (this.container !== container) return;

      // Render appropriate UI based on whether photo exists
      if (existingUrl) {
        container.innerHTML = this._renderPreview(existingUrl);
      } else {
        container.innerHTML = this._renderUploader();
        await this._initWidget();
      }
    } catch (error) {
      container.innerHTML = this._renderError(error.message);
    }
  }

  /**
   * Render loading state
   */
  _renderLoading() {
    return `
      <div class="bundle-photo-loading text-center py-4">
        <div class="spinner-border spinner-border-sm text-secondary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="small text-muted mt-2 mb-0">Loading photo uploader...</p>
      </div>
    `;
  }

  /**
   * Render uploader widget
   */
  _renderUploader() {
    const containerId = this.currentMode === 'together'
      ? 'bundle-uploadcare-together'
      : `bundle-uploadcare-individual-${this.currentProductIndex}`;

    return `
      <div class="bundle-photo-dropzone" id="${containerId}">
        <input type="hidden"
               role="uploadcare-uploader"
               data-public-key="${BundlePhotoUploader.UPLOADCARE_PUBLIC_KEY}"
               data-images-only="true"
               data-preview-step="true"
               data-crop="free"
               data-image-shrink="1024x1024"
               data-tabs="file camera url"
               data-clearable="true" />
        <div class="bundle-photo-prompt">
          <svg class="mb-2" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"/>
          </svg>
          <p class="mb-1">Click to upload photo</p>
          <small class="text-muted">or drag and drop • JPG, PNG up to 10MB</small>
        </div>
      </div>
    `;
  }

  /**
   * Render photo preview
   * @param {string} photoUrl - Photo URL
   */
  _renderPreview(photoUrl) {
    // Add resize transform for preview thumbnail
    const thumbnailUrl = BundlePhotoUploader.escapeAttribute(`${photoUrl}-/resize/200x/`);

    return `
      <div class="bundle-photo-preview">
        <img src="${thumbnailUrl}" alt="Uploaded photo" class="bundle-photo-thumb">
        <div class="bundle-photo-actions">
          <span class="text-success d-flex align-items-center gap-1">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
            </svg>
            Photo uploaded
          </span>
          <button type="button" class="btn btn-sm btn-outline-secondary" data-bundle-change-photo>
            Change Photo
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Render error state
   * @param {string} message - Error message
   */
  _renderError(message) {
    return `
      <div class="bundle-photo-error alert alert-warning py-3 mb-0">
        <p class="mb-2 small">Unable to load photo uploader.</p>
        <button type="button" class="btn btn-sm btn-outline-secondary" data-bundle-retry-photo>
          Try Again
        </button>
      </div>
    `;
  }

  /**
   * Initialize UploadCare widget
   */
  async _initWidget() {
    if (!window.uploadcare || !this.container) return;

    const input = this.container.querySelector('input[role="uploadcare-uploader"]');
    if (!input) return;

    try {
      // Initialize widget
      this.widget = window.uploadcare.Widget(input);

      // BUG-050 FIX: Capture upload context for registry fallback
      const uploadContext = {
        uploadId: this.uploadId,
        mode: this.currentMode,
        productIndex: this.currentProductIndex,
        stateManager: this.stateManager,
        callback: this.onUploadCallback,
        expiresAt: Date.now() + BundlePhotoUploader.UPLOAD_TIMEOUT_MS
      };

      // Listen for upload completion
      this.widget.onUploadComplete((fileInfo) => {
        this._handleUploadComplete(fileInfo, uploadContext);
      });

      // Listen for file selection (register in pending uploads)
      this.widget.onChange((file) => {
        if (file) {
          // BUG-050 FIX: Register when user selects a file
          BundlePhotoUploader.pendingUploads.set(this.uploadId, uploadContext);

          // Show loading indicator while processing
          const dropzone = this.container?.querySelector('.bundle-photo-dropzone');
          if (dropzone) {
            dropzone.classList.add('is-processing');
          }
        }
      });

    } catch (error) {
      this.container.innerHTML = this._renderError('Widget initialization failed');
    }
  }

  /**
   * Handle successful upload
   * @param {Object} fileInfo - UploadCare file info
   * @param {Object} uploadContext - BUG-050 FIX: Captured context for fallback
   */
  _handleUploadComplete(fileInfo, uploadContext) {
    if (!fileInfo?.cdnUrl) return;

    const photoUrl = fileInfo.cdnUrl;

    // BUG-050 FIX: Remove from pending registry
    if (uploadContext?.uploadId) {
      BundlePhotoUploader.pendingUploads.delete(uploadContext.uploadId);
    }

    // Update UI to show preview (if container still exists)
    if (this.container) {
      this.container.innerHTML = this._renderPreview(photoUrl);
    }

    // PRIMARY PATH: Use instance callback if still valid
    if (this.onUploadCallback) {
      this.onUploadCallback(photoUrl);
      return;
    }

    // BUG-050 FIX: FALLBACK PATH - Instance was cleaned up, use captured context
    // Quick view is skipped: its callback writes shared modal state, which may now belong to another product
    if (uploadContext?.callback && uploadContext?.stateManager && uploadContext.mode !== 'quickview') {
      console.info('[BundlePhotoUploader] Using registry fallback to save photo after cleanup');

      // Validate and execute the captured callback
      try {
        uploadContext.callback(photoUrl);
      } catch (e) {
        console.warn('[BundlePhotoUploader] Fallback callback failed:', e);
      }
    }
  }

  /**
   * Bind the delegated Change Photo / Try Again handler (once per container)
   */
  _bindChangeHandler() {
    if (!this.container || this._boundChangeHandler) return;

    this._boundChangeHandler = (e) => {
      if (e.target.closest('[data-bundle-change-photo]')) {
        this._handleChangePhoto();
      } else if (e.target.closest('[data-bundle-retry-photo]')) {
        this._handleRetry();
      }
    };

    this.container.addEventListener('click', this._boundChangeHandler);
  }

  /**
   * Handle Try Again click on the error state
   */
  _handleRetry() {
    if (!this.container) return;

    if (this.currentMode === 'quickview') {
      this.initForQuickView(this.container, this.onUploadCallback);
    } else {
      this._render(this.getPhotoUrl());
    }
  }

  /**
   * Handle change photo click
   */
  async _handleChangePhoto() {
    // Clear current photo from state
    if (this.onUploadCallback) {
      this.onUploadCallback(null);
    }

    // Re-render uploader
    if (this.container) {
      this.container.innerHTML = this._renderUploader();
      await this._initWidget();
    }
  }
}

// Export for other modules
window.BundlePhotoUploader = BundlePhotoUploader;
